import { nowAsEpoch, computeStartEndForContract } from './utils';

const responseSizeLimit = 700;

const granularities = [60, 120, 180, 300, 600, 900, 1800, 3600, 7200, 14400, 28800, 86400];

const ohlcDataToTicks = (candles) => candles.map(data => ({ quote: +data.open, epoch: +data.epoch }));

export const autoAdjustGetData = (
    api,
    symbol,
    start,
    end,
    style = 'ticks',
    subscribe,
    extra = {}
) => {
    const secs = end - start;
    const ticksCount = secs / 2;
    if (ticksCount >= responseSizeLimit || style === 'candles') {
        const idealGranularity = secs / responseSizeLimit;
        let finalGranularity = 60;
        granularities.forEach((g, i) => {
            if (idealGranularity > g && (i === granularities.length - 1 || idealGranularity <= granularities[i + 1])) {
                finalGranularity = i === granularities.length - 1 ? g : granularities[i + 1];
            }
        });
        finalGranularity = Math.min(86400, finalGranularity);

        return api
            .getTickHistory(symbol, {
                start,
                end,
                adjust_start_time: 1,
                count: responseSizeLimit,
                style: 'candles',
                granularity: finalGranularity,
                subscribe: subscribe ? 1 : undefined,
            })
            .then(r => {
                if (style === 'ticks') {
                    return {
                        ...extra,
                        ticks: ohlcDataToTicks(r.candles),
                        symbol,
                    };
                }
                return {
                    ...extra,
                    candles: r.candles,
                    symbol,
                };
            });
    }
    return api
        .getTickHistory(symbol, {
            start,
            end,
            adjust_start_time: 1,
            count: responseSizeLimit,
            style: 'ticks',
            subscribe: subscribe ? 1 : undefined,
        })
        .then(r => {
            const ticks = r.history.times.map((t, idx) => {
                const quote = r.history.prices[idx];
                return { epoch: +t, quote: +quote };
            });
            return {
                ...extra,
                ticks,
                symbol,
            };
        });
};

export function getDataForSymbol(
    api,
    symbol,
    duration = 600,
    style = 'ticks',
    subscribe
) {
    const end = nowAsEpoch();
    const start = end - duration;
    return autoAdjustGetData(api, symbol, start, end, style, subscribe);
}

export function getDataForContract(
    api,
    getContract,
    duration,
    style = 'ticks',
    subscribe
) {
    const getAllData = () =>
        getContract().then(contract => {
            const symbol = contract.underlying;
            const { start, end } = computeStartEndForContract(contract);
            return autoAdjustGetData(api, symbol, start, end, style, subscribe, { isSold: !!contract.sell_time });
        });

    if (!duration) {
        return getAllData();
    }

    return getContract().then(contract => {
        const symbol = contract.underlying;
        const startTime = +contract.date_start;

        if (startTime > nowAsEpoch()) {
            return autoAdjustGetData(api, symbol, nowAsEpoch() - 600, nowAsEpoch(), style, subscribe, {
                isSold: !!contract.sell_time,
            });
        }

        const sellT = contract.sell_time;
        const end = sellT || nowAsEpoch();

        const buffer = (end - startTime) * 0.05;

        const start = Math.min(startTime - buffer, end - duration);
        return autoAdjustGetData(api, symbol, Math.round(start), Math.round(end), style, subscribe, {
            isSold: !!contract.sell_time,
        });
    });
}

export const helpers = {
    autoAdjustGetData,
};
